import { Add, Cloud, Email, Refresh, Settings } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { IUserCalendarConnection } from '../Interfaces/IUserCalendarConnection';

interface IProps {
  connections: IUserCalendarConnection[];
  /** Connection-IDs, deren Events aktuell sichtbar sind. */
  visibleIds: Set<number>;
  onToggle: (idUserCalendarConnection: number) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onManage: () => void;
  onAdd: () => void;
  onReload: () => void;
  isReloading?: boolean;
}

const PROVIDER_ICON: Record<string, React.ReactNode> = {
  o365: <Email fontSize='small' />,
  webdav: <Cloud fontSize='small' />,
};

/**
 * Linke Sidebar der Kalenderansicht — Liste aller hinterlegten Kalender mit
 * Farbpunkt, Sichtbarkeits-Checkbox und Provider-Icon. Knöpfe für „Alle ein/aus",
 * Hinzufügen, Verwalten und Aktualisieren am Ende.
 */
export const CalendarSidebar: React.FC<IProps> = ({
  connections,
  visibleIds,
  onToggle,
  onShowAll,
  onHideAll,
  onManage,
  onAdd,
  onReload,
  isReloading,
}) => {
  // Falls das Backend keine Farbe geliefert hat, weisen wir deterministisch
  // basierend auf der Connection-ID eine aus einer Default-Palette zu, damit
  // Events trotzdem farblich unterscheidbar sind.
  const colorFor = (conn: IUserCalendarConnection): string => {
    const fromCfg = conn.Config?._color as string | undefined;
    if (fromCfg) return fromCfg;
    const palette = [
      '#1f6feb',
      '#16a34a',
      '#dc2626',
      '#9333ea',
      '#ea580c',
      '#0891b2',
      '#db2777',
      '#65a30d',
    ];
    return palette[conn.idUserCalendarConnection % palette.length];
  };

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ px: 2, py: 1.5 }}
      >
        <Typography variant='subtitle1' fontWeight={600}>
          Meine Kalender
        </Typography>
        <Tooltip title='Aktualisieren'>
          <span>
            <IconButton size='small' onClick={onReload} disabled={isReloading}>
              {isReloading ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh fontSize='small' />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {connections.length > 1 && (
        <Stack direction='row' spacing={1} sx={{ px: 2, pb: 1 }}>
          <Button size='small' onClick={onShowAll}>
            Alle einblenden
          </Button>
          <Button size='small' onClick={onHideAll}>
            Alle ausblenden
          </Button>
        </Stack>
      )}

      <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
        {connections.map(conn => {
          const visible = visibleIds.has(conn.idUserCalendarConnection);
          const color = colorFor(conn);
          return (
            <ListItem
              key={conn.idUserCalendarConnection}
              disablePadding
              secondaryAction={
                <Tooltip
                  title={
                    conn.IsActive
                      ? `Verbunden (${conn.Provider})`
                      : 'Verbindung deaktiviert'
                  }
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      pr: 1,
                      opacity: conn.IsActive ? 0.65 : 0.3,
                    }}
                  >
                    {PROVIDER_ICON[conn.Provider]}
                  </Box>
                </Tooltip>
              }
            >
              <ListItemButton
                onClick={() => onToggle(conn.idUserCalendarConnection)}
                sx={{ py: 0.5 }}
              >
                <Checkbox
                  edge='start'
                  size='small'
                  checked={visible}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    color,
                    '&.Mui-checked': { color },
                    p: 0.5,
                    mr: 1,
                  }}
                />
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: color,
                    mr: 1,
                    flexShrink: 0,
                    opacity: conn.IsActive ? 1 : 0.4,
                  }}
                />
                <Typography
                  variant='body2'
                  noWrap
                  sx={{
                    flex: 1,
                    color: conn.IsActive ? 'text.primary' : 'text.disabled',
                    textDecoration: conn.IsActive ? 'none' : 'line-through',
                  }}
                  title={conn.Label}
                >
                  {conn.Label}
                </Typography>
                {conn.IsDefault && (
                  <Chip
                    size='small'
                    color='warning'
                    label='Std'
                    sx={{ ml: 0.5 }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Stack spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<Add />}
          onClick={onAdd}
        >
          Kalender hinzufügen
        </Button>
        <Button size='small' startIcon={<Settings />} onClick={onManage}>
          Verwalten
        </Button>
      </Stack>
    </Box>
  );
};
